import type { Offer } from '@/api/types';
import type { CountryConversion } from '@/api/stats';
import { COUNTRY_MAP } from '@/lib/countries';

export interface ExportRow {
  code: string;
  country: string;
  partner: string;
  brand: string;
  source: string;
  vertical: string;
  payout: string;
  currency: string;
  rs: string;
  mindep: string;
  kpi: string;
  kpiText: string;
  wager: string;
  cap: string;
  capDeps: string;
  status: string;
  offerUrl: string;
  clicks: string;
  regs: string;
  deps: string;
  regPct: string;
  depPct: string;
  revenue: string;
  epc: string;
}

export type ColumnKey = keyof ExportRow;

export interface ColumnDef {
  key: ColumnKey;
  header: string;
  group: 'offer' | 'performance';
  numeric: boolean;
}

export const COLUMNS: ColumnDef[] = [
  { key: 'code', header: 'Code', group: 'offer', numeric: false },
  { key: 'country', header: 'Country', group: 'offer', numeric: false },
  { key: 'partner', header: 'Partner', group: 'offer', numeric: false },
  { key: 'brand', header: 'Brand', group: 'offer', numeric: false },
  { key: 'source', header: 'Source', group: 'offer', numeric: false },
  { key: 'vertical', header: 'Vertical', group: 'offer', numeric: false },
  { key: 'payout', header: 'Payout', group: 'offer', numeric: true },
  { key: 'currency', header: 'Currency', group: 'offer', numeric: false },
  { key: 'rs', header: 'RS', group: 'offer', numeric: false },
  { key: 'mindep', header: 'Mindep', group: 'offer', numeric: false },
  { key: 'kpi', header: 'KPI', group: 'offer', numeric: false },
  { key: 'kpiText', header: 'KPI text', group: 'offer', numeric: false },
  { key: 'wager', header: 'Wager', group: 'offer', numeric: false },
  { key: 'cap', header: 'Cap', group: 'offer', numeric: true },
  { key: 'capDeps', header: 'Cap deps', group: 'offer', numeric: true },
  { key: 'status', header: 'Status', group: 'offer', numeric: false },
  { key: 'offerUrl', header: 'Offer URL', group: 'offer', numeric: false },
  { key: 'clicks', header: 'Clicks', group: 'performance', numeric: true },
  { key: 'regs', header: 'Regs', group: 'performance', numeric: true },
  { key: 'deps', header: 'Deps', group: 'performance', numeric: true },
  { key: 'regPct', header: 'Reg%', group: 'performance', numeric: true },
  { key: 'depPct', header: 'Dep%', group: 'performance', numeric: true },
  { key: 'revenue', header: 'Revenue', group: 'performance', numeric: true },
  { key: 'epc', header: 'EPC', group: 'performance', numeric: true },
];

export const ALL_COLUMN_KEYS: ColumnKey[] = COLUMNS.map((c) => c.key);

function geoCodes(geo: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of (geo || '').split(',')) {
    const code = raw.trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

export function buildExportRows(
  offers: Offer[],
  conv: Map<string, CountryConversion>,
): ExportRow[] {
  const rows: ExportRow[] = [];
  for (const o of offers) {
    const codes = geoCodes(o.geo);
    const targets = codes.length > 0 ? codes : [''];
    for (const code of targets) {
      // Conversion is looked up by the offer's own geo code only; the endpoint's
      // 'UNKNOWN'-country bucket (traffic with no country) is intentionally not
      // attributed to any offer, so it never appears in the export.
      const cc = code ? conv.get(code) : undefined;
      const clicks = cc?.clicks ?? 0;
      const regs = cc?.regs ?? 0;
      const deps = cc?.deps ?? 0;
      const revenue = (cc?.revenue_cents ?? 0) / 100;
      const pct = (num: number) => (clicks > 0 ? ((num / clicks) * 100).toFixed(2) : '');
      rows.push({
        code,
        country: code ? (COUNTRY_MAP[code]?.name || code) : '',
        partner: o.partner || '',
        brand: o.brand || '',
        source: o.traffic_source || '',
        vertical: o.vertical || '',
        payout: o.payout_cents != null ? (o.payout_cents / 100).toFixed(2) : '',
        currency: o.currency || '',
        rs: o.rs || '',
        mindep: o.mindep_bl || '',
        kpi: o.kpi || '',
        kpiText: o.kpi_text || '',
        wager: o.wager || '',
        cap: o.cap != null ? String(o.cap) : '',
        capDeps: String(o.cap_deps ?? 0),
        status: o.status || '',
        offerUrl: o.offer_url || '',
        clicks: String(clicks),
        regs: String(regs),
        deps: String(deps),
        regPct: pct(regs),
        depPct: pct(deps),
        revenue: revenue.toFixed(2),
        epc: clicks > 0 ? (revenue / clicks).toFixed(2) : '',
      });
    }
  }
  return rows;
}

// CSV formula-injection guard: a cell starting with one of these is treated as
// a formula by Excel/Sheets. Prefix with an apostrophe to force it to stay text.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

function neutralizeFormula(value: string): string {
  return FORMULA_TRIGGER.test(value) ? "'" + value : value;
}

function escapeCsvField(value: string): string {
  const safe = neutralizeFormula(value);
  if (/[",\n\r]/.test(safe)) {
    return '"' + safe.replace(/"/g, '""') + '"';
  }
  return safe;
}

export function toCsv(rows: ExportRow[], selectedKeys: ColumnKey[] = ALL_COLUMN_KEYS): string {
  const keys = new Set(selectedKeys);
  const cols = COLUMNS.filter((c) => keys.has(c.key));
  const lines = [cols.map((c) => escapeCsvField(c.header)).join(',')];
  for (const row of rows) {
    lines.push(cols.map((c) => escapeCsvField(row[c.key])).join(','));
  }
  return '﻿' + lines.join('\r\n');
}

// SheetJS is ~100KB+ gzipped, so it's dynamically imported here — the chunk is
// only fetched when a user actually exports XLSX, not on every Offers visit.
export async function toXlsx(
  rows: ExportRow[],
  selectedKeys: ColumnKey[] = ALL_COLUMN_KEYS,
): Promise<Uint8Array> {
  const XLSX = await import('xlsx');
  const keys = new Set(selectedKeys);
  const cols = COLUMNS.filter((c) => keys.has(c.key));
  const aoa: (string | number | null)[][] = [cols.map((c) => c.header)];
  for (const row of rows) {
    aoa.push(
      cols.map((c) => {
        const raw = row[c.key];
        if (raw === '') return null; // blank cell
        return c.numeric ? Number(raw) : raw; // numeric vs text cell
      }),
    );
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Offers');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as Uint8Array;
}

export function downloadXlsx(filename: string, data: Uint8Array): void {
  const blob = new Blob([data as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
